import React from 'react';
import { AlertCircle, CheckCircle2, Upload, X } from 'lucide-react';
import { MAX_LEAD_DOCUMENT_UPLOAD_BYTES } from '../utils/leadDocumentFiles';
import { DocumentUploadProgressBanner, type DocumentUploadProgress } from './DocumentUploadProgress';

export type LeadFileDropFeedback =
  | { kind: 'uploading' }
  | { kind: 'done'; count: number }
  | { kind: 'mixed'; uploadedCount: number; errors: string }
  | { kind: 'error'; message: string };

/** Ganzseitiges Drop-Ziel-Feedback der Projektseite plus Status des ausgelösten Uploads. */
export function LeadFileDropLayer({
  dragActive,
  feedback,
  progress,
  onDismiss,
}: {
  dragActive: boolean;
  feedback: LeadFileDropFeedback | null;
  progress: DocumentUploadProgress | null;
  onDismiss: () => void;
}) {
  React.useEffect(() => {
    if (feedback?.kind !== 'done') return;
    const timer = window.setTimeout(onDismiss, 4000);
    return () => window.clearTimeout(timer);
  }, [feedback, onDismiss]);

  const maxMb = Math.round(MAX_LEAD_DOCUMENT_UPLOAD_BYTES / (1024 * 1024));

  return (
    <>
      {dragActive ? (
        <div className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center bg-sky-500/10 p-6">
          <div className="rounded-2xl border-2 border-dashed border-sky-400 bg-app-surface/95 px-8 py-6 text-center shadow-xl">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-sky-500">
              <Upload className="h-6 w-6" />
            </div>
            <p className="text-sm font-semibold text-app-text">Dateien hier ablegen</p>
            <p className="mt-1 text-xs text-app-text-subtle">
              Werden direkt zum Projekt hochgeladen · max. {maxMb} MB pro Datei
            </p>
          </div>
        </div>
      ) : null}

      {feedback ? (
        <div className="fixed bottom-4 right-4 z-[60] w-80 max-w-[calc(100vw-2rem)]">
          {feedback.kind === 'uploading' ? <DocumentUploadProgressBanner progress={progress} /> : null}
          {feedback.kind === 'done' ? (
            <div
              className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 shadow-sm"
              role="status"
              aria-live="polite"
            >
              <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" />
              <p className="text-sm font-medium text-emerald-800">
                {feedback.count === 1 ? 'Datei hochgeladen.' : `${feedback.count} Dateien hochgeladen.`}
              </p>
            </div>
          ) : null}
          {feedback.kind === 'mixed' ? (
            <div className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 shadow-sm">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-amber-900">
                    {feedback.uploadedCount === 1 ? '1 Datei hochgeladen' : `${feedback.uploadedCount} Dateien hochgeladen`}
                  </p>
                  <p className="mt-1 text-sm text-amber-800 break-words">{feedback.errors}</p>
                </div>
              </div>
            </div>
          ) : null}
          {feedback.kind === 'error' ? (
            <div
              className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 shadow-sm"
              role="alert"
            >
              <AlertCircle className="w-5 h-5 shrink-0 text-red-600" />
              <p className="min-w-0 flex-1 text-sm text-red-800 break-words">{feedback.message}</p>
              <button
                type="button"
                onClick={onDismiss}
                className="shrink-0 rounded-md p-1 text-red-600 hover:bg-red-100"
                aria-label="Meldung schließen"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
